import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik } from 'formik';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Container, Row, Table, UncontrolledTooltip } from 'reactstrap';
import * as Yup from 'yup';
import adminService from '../../../services/adminService';
import { GeneralSetting, GeneralSettingFilter } from '../../../services/types/Admin';
import { LinkButton, PageHeader, Section, SpinnerArea } from '../../../shared/components';
import useServiceReducer from '../../../shared/hooks/useServiceReducer';
import { dateTimeFormat, tooltipPreventOverflowModifiers } from '../../../shared/misc/utils';
import { KeyMatch } from '../../../types';
import MainContext from '../../App/MainProvider/context';
import RouteLeavingGuard from '../../App/RouteLeavingGuard';
import reducer from './reducer';

const ARROW_DOWN = 'arrow-down';
const ARROW_UP = 'arrow-up';

const GeneralSettings: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const { service, notify } = useContext(MainContext);

  const [formDirty, setFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [inEditMode, setInEditMode] = useState<{ status: boolean; rowKey: string | null }>({
    status: false,
    rowKey: null,
  });

  const onEditMode = (id: string) => {
    setInEditMode({
      status: true,
      rowKey: id,
    });
  };

  const onCancel = () => {
    setInEditMode({
      status: false,
      rowKey: null,
    });

    setFormDirty(false);
  };

  const initFilter: GeneralSettingFilter = {
    sortField: 'name' as KeyMatch<GeneralSetting, string>,
    sortDescending: false,
  };

  const [data, loading, , dispatch, , reload] = useServiceReducer(
    adminService.getGeneralSettings,
    reducer,
    undefined
  );

  const [filter, setFilter] = useState(initFilter);

  const changeSort = (items: GeneralSetting[], column: KeyMatch<GeneralSetting, string>) => {
    const descending = filter.sortDescending;
    const sortedList = [...items].sort((a, b) => {
      if ((descending && a[column] === null) || (!descending && b[column] === null)) {
        return -1;
      }
      if ((descending && b[column] === null) || (!descending && a[column] === null)) {
        return 1;
      }
      if (column === ('modifiedOn' as KeyMatch<GeneralSetting, string>)) {
        if (
          (descending && new Date(a[column]).getTime() < new Date(b[column]).getTime()) ||
          (!descending && new Date(a[column]).getTime() > new Date(b[column]).getTime())
        ) {
          return -1;
        }
        if (
          (descending && new Date(a[column]).getTime() > new Date(b[column]).getTime()) ||
          (!descending && new Date(a[column]).getTime() < new Date(b[column]).getTime())
        ) {
          return 1;
        }
      } else {
        if ((descending && a[column] < b[column]) || (!descending && a[column] > b[column])) {
          return -1;
        }
        if ((descending && a[column] > b[column]) || (!descending && a[column] < b[column])) {
          return 1;
        }
      }
      return 0;
    });

    dispatch({ type: 'ORDER', data: sortedList });

    setFilter((prev) => ({ ...filter, sortField: column, sortDescending: !prev.sortDescending }));
  };

  const saveChanges = (setting: GeneralSetting) => {
    if (!isSubmitting) {
      setIsSubmitting(true);
      service(() => adminService.updateGeneralSetting(setting.id, setting), {
        callback: () => {
          notify(t('generalSettings'), t('savedSuccessfully'), 'success');
          reload();
          setFormDirty(false);
          setInEditMode({
            status: false,
            rowKey: null,
          });
        },
        loader: setIsSubmitting,
      });
    }
  };

  return (
    <>
      <RouteLeavingGuard
        when={formDirty}
        shouldBlockNavigation={() => formDirty}
        from={location}
        message={t('dirtyAdminPopupMsg')}
      />
      <Container className="general-settings-container">
        <PageHeader title={t('generalSettings')} noBack />
        <Row>
          <Section>
            <SpinnerArea loading={loading}>
              {data && (
                <div className="table-wrapper sticky-section w-100">
                  <Table className="general-settings-table table--bordered table-hover table-header-sticky sortable">
                    <thead>
                      <tr>
                        <th
                          style={{ width: '15%' }}
                          onClick={() => {
                            changeSort(data, 'name');
                          }}
                          className={`sortable${filter.sortField === 'name' ? ' current' : ''}`}
                        >
                          <span>
                            {t('name')}
                            {filter.sortField === 'name' && (
                              <FontAwesomeIcon
                                icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                              />
                            )}
                          </span>
                        </th>
                        <th
                          style={{ width: '15%' }}
                          onClick={() => {
                            changeSort(data, 'description');
                          }}
                          className={`sortable${
                            filter.sortField === 'description' ? ' current' : ''
                          }`}
                        >
                          <span>
                            {t('description')}
                            {filter.sortField === 'description' && (
                              <FontAwesomeIcon
                                icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                              />
                            )}
                          </span>
                        </th>
                        <th
                          style={{ width: '40%' }}
                          onClick={() => {
                            changeSort(data, 'value');
                          }}
                          className={`sortable${filter.sortField === 'value' ? ' current' : ''}`}
                        >
                          <span>
                            {t('value')}
                            {filter.sortField === 'value' && (
                              <FontAwesomeIcon
                                icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                              />
                            )}
                          </span>
                        </th>
                        <th
                          style={{ width: '10%' }}
                          onClick={() => {
                            changeSort(data, 'modifiedOn');
                          }}
                          className={`sortable${
                            filter.sortField === 'modifiedOn' ? ' current' : ''
                          }`}
                        >
                          <span>
                            {t('modifiedDate')}
                            {filter.sortField === 'modifiedOn' && (
                              <FontAwesomeIcon
                                icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                              />
                            )}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((x) => {
                        const key = `tr_general_setting_${x.id}`;
                        return (
                          <tr key={key}>
                            <td>{x.name}</td>
                            <td>{x.description}</td>
                            {inEditMode.status && inEditMode.rowKey === key ? (
                              <td className="setting-in-edit-mode">
                                <Formik
                                  initialValues={{
                                    newSetting: x.value || '',
                                  }}
                                  validationSchema={Yup.object().shape({
                                    newSetting: Yup.string()
                                      .required(t('required'))
                                      .max(400, t('validateMaxLength', { len: 400 })),
                                  })}
                                  onSubmit={(values) => {
                                    saveChanges({ ...x, value: values.newSetting });
                                  }}
                                >
                                  {(formik) => (
                                    <form onSubmit={formik.handleSubmit} className="form">
                                      <div className="form__form-group">
                                        <div className="form__form-group-field d-inline-flex align-items-center">
                                          <span
                                            id={`setting_icon_${key}`}
                                            className="setting-hint-info_icon"
                                          >
                                            <FontAwesomeIcon icon="info-circle" />
                                          </span>
                                          <UncontrolledTooltip
                                            className="tooltip-impact"
                                            target={`setting_icon_${key}`}
                                            modifiers={tooltipPreventOverflowModifiers}
                                          >
                                            <p>{x.hint}</p>
                                          </UncontrolledTooltip>

                                          <div className="form__form-group-input-wrap">
                                            <input
                                              name="newSetting"
                                              className={formik.errors.newSetting ? 'error' : ''}
                                              placeholder={x.placeholder}
                                              type="text"
                                              value={formik.values.newSetting || ''}
                                              autoComplete="off"
                                              onChange={(e) => {
                                                formik.handleChange(e);
                                                setFormDirty(e.target.value !== x.value);
                                              }}
                                              onBlur={formik.handleBlur}
                                              required
                                              maxLength={450}
                                            />
                                            {formik.errors.newSetting && (
                                              <span className="form__form-group-error">
                                                {formik.errors.newSetting}
                                              </span>
                                            )}
                                          </div>
                                          <div className="d-flex flex-row">
                                            <button
                                              title="submit"
                                              className="edit-button ok"
                                              type="submit"
                                              disabled={
                                                !!formik.errors.newSetting ||
                                                isSubmitting ||
                                                !formDirty
                                              }
                                            >
                                              <FontAwesomeIcon icon="check" />
                                            </button>
                                            <button
                                              title="cancel"
                                              className="edit-button cancel"
                                              type="button"
                                              onClick={onCancel}
                                            >
                                              <FontAwesomeIcon icon="times" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </form>
                                  )}
                                </Formik>
                              </td>
                            ) : (
                              <td>
                                <LinkButton
                                  text={x.value}
                                  onClick={() => {
                                    onEditMode(key);
                                  }}
                                  noFit
                                  width="700px"
                                />
                              </td>
                            )}
                            <td>{x.modifiedOn ? dateTimeFormat(x.modifiedOn) : 'N/A'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </SpinnerArea>
          </Section>
        </Row>
      </Container>
    </>
  );
};

export default GeneralSettings;
